import React from "react";
import { NavLink } from "react-router-dom";

/**
 * Section shell: left rail (primary portal links + optional in-section links) + main content.
 * Pairs with ClientPortalLayout header (My projects / Real estate / Main website).
 *
 * @param {"default" | "realEstate"} [presentation] — CL-325: premium styling for real-estate workspace only.
 * CL-334: real-estate rail + content spacing and squarer, less “SaaS card” chrome on the rail.
 */
export default function ClientPortalSectionShell({
  navLabel,
  items,
  subItems,
  subItemsLabel,
  children,
  presentation = "default",
}) {
  const isRe = presentation === "realEstate";

  const subNavClass = ({ isActive }) =>
    isRe
      ? `block shrink-0 rounded-md px-3 py-2.5 text-sm font-semibold no-underline transition-all whitespace-nowrap md:whitespace-normal ${
          isActive
            ? "bg-slate-900 text-white ring-1 ring-emerald-400/30"
            : "border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-stone-50 hover:text-slate-900"
        }`
      : `block shrink-0 rounded-lg px-3 py-2 text-sm font-medium no-underline transition-colors whitespace-nowrap md:whitespace-normal ${
          isActive
            ? "bg-slate-900 text-white shadow-sm"
            : "border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
        }`;

  const subNavClassNested = ({ isActive }) =>
    isRe
      ? `block shrink-0 rounded-lg px-3 py-1.5 text-sm no-underline transition-all whitespace-nowrap md:whitespace-normal ${
          isActive
            ? "bg-slate-900 text-white font-medium shadow-sm"
            : "text-slate-600 hover:bg-emerald-50/90 hover:text-slate-900"
        }`
      : `block shrink-0 rounded-md px-3 py-1.5 text-sm no-underline transition-colors whitespace-nowrap md:whitespace-normal ${
          isActive
            ? "bg-slate-800 text-white font-medium"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        }`;

  const outerClass = isRe
    ? "mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-5 md:flex-row md:items-start md:gap-12 lg:gap-14 md:px-6 md:py-9"
    : "mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 md:flex-row md:items-start md:gap-8 md:px-6 md:py-6";

  const navClass = isRe
    ? "shrink-0 md:sticky md:top-24 md:z-40 md:w-56 md:max-h-[calc(100vh-7rem)] md:overflow-y-auto md:self-start md:rounded-md md:border md:border-slate-200 md:bg-white md:p-4 md:shadow-sm md:shadow-slate-900/[0.03] md:backdrop-blur-sm supports-[backdrop-filter]:md:bg-white/98"
    : "shrink-0 md:sticky md:top-24 md:z-40 md:w-56 md:max-h-[calc(100vh-7rem)] md:overflow-y-auto md:self-start md:rounded-xl md:border md:border-slate-200/90 md:bg-white/95 md:p-3 md:shadow-sm md:backdrop-blur-sm supports-[backdrop-filter]:md:bg-white/90";

  return (
    <div className={outerClass}>
      <nav className={navClass} aria-label={navLabel}>
        <ul className="-mx-1 flex flex-row gap-2 overflow-x-auto px-1 pb-1 md:mx-0 md:flex-col md:gap-1.5 md:overflow-visible md:px-0 md:pb-0">
          {items.map((it) => (
            <li key={it.to} className="shrink-0 md:shrink">
              <NavLink to={it.to} end={Boolean(it.end)} className={subNavClass}>
                {it.label}
              </NavLink>
            </li>
          ))}
        </ul>
        {subItems?.length ? (
          <div className={`mt-3 border-t pt-3 ${isRe ? "border-slate-200/80" : "border-slate-200"}`}>
            {subItemsLabel ? (
              <p
                className={`mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-wide ${isRe ? "text-slate-500" : "text-slate-500"}`}
              >
                {subItemsLabel}
              </p>
            ) : null}
            <ul
              className={`flex flex-row gap-1 overflow-x-auto md:flex-col md:gap-0.5 md:overflow-visible md:border-l md:pl-2 ${isRe ? "md:border-emerald-100" : "md:border-slate-200"}`}
            >
              {subItems.map((it) => (
                <li key={it.to} className="shrink-0 md:shrink">
                  <NavLink to={it.to} end={Boolean(it.end)} className={subNavClassNested}>
                    {it.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </nav>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
